<template>
    <b-container fluid>
        <!-- 나가는 길: 문서 사실 추출 화면에서 실어 온 기간·프로젝트·업체로 돌아간다($router.back 은 직접 들어온 경우 틀린다) -->
        <admin-page-header title="문서 사실 추출 배치 관리"
                           description="첨부문서를 AI(OpenAI·Google)에 한꺼번에 보내 내용을 자동으로 뽑아내는 작업 목록입니다. 결과는 대부분 저절로 채워지며, 특정 배치가 오래 멈춰 있는 것 같을 때만 그 줄의 「지금 동기화」를 눌러 결과를 다시 받아오세요."
                           :back="{label: '문서 사실 추출로 돌아가기', to: {name: 'DocumentFacts', query: selectionQuery}}">
            <template #actions>
                <span class="dfb-auto-tag mr-3"
                      title="처리 중인 배치가 있으면 60초마다 자동으로 최신 상태를 불러옵니다"
                >
                    <span class="dfb-auto-dot"/>60초마다 자동 갱신
                </span>
                <span v-if="silentRefreshing" class="text-muted small mr-2">
                    <b-spinner small class="mr-1"/>갱신 중
                </span>
                <b-btn variant="outline-secondary" size="sm" :disabled="loading"
                       @click="refresh()"
                >
                    <b-spinner v-if="loading" small class="mr-1"/>
                    <b-icon-arrow-clockwise v-else class="mr-1"/>
                    새로고침
                </b-btn>
            </template>
        </admin-page-header>

        <!-- 현황 요약 (KPI 스트립) — 작업 단위 개요 + 배치 요약을 한 줄로 통합 -->
        <b-card class="mb-3 dfb-kpi-card" no-body>
            <div class="d-flex align-items-center justify-content-between px-3 pt-2">
                <strong class="small">현황 요약</strong>
                <span class="text-muted small">
                    <template v-if="overview && overview.generated_at">
                        기준 {{ getDate(overview.generated_at) }} · 최근 {{ overviewLookbackHours }}시간
                    </template>
                    <b-spinner v-if="overviewLoading" small class="ml-1"/>
                </span>
            </div>
            <div class="dfb-kpi-strip">
                <div v-for="card in kpiCards" :key="card.key" class="dfb-kpi">
                    <span class="text-muted small d-block">{{ card.label }}</span>
                    <span class="dfb-kpi-val" :class="`text-${card.variant}`">
                        {{ formatNum(card.value) }}<span class="dfb-kpi-unit">건</span>
                    </span>
                </div>
            </div>
        </b-card>

        <!-- 운영 개요 상세 (진단용, 기본 접힘) -->
        <b-card class="mb-3" no-body>
            <div class="dfb-collapse-head" @click="toggleOverviewDetail">
                <b-icon-chevron-right :rotate="overviewDetailOpen ? 90 : 0" class="mr-2 text-muted"/>
                <strong>운영 개요 상세</strong>
                <span class="text-muted small ml-2">
                    상태 분포 · 실패 사유 · 프로젝트별 (진단용 · 필요할 때만 펼침)
                </span>
            </div>
            <b-collapse v-model="overviewDetailOpen">
                <div class="dfb-collapse-body px-3 pb-3">
                    <b-row>
                        <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
                            <div class="text-muted small mb-1">상태 분포 (active / 최근 종료)</div>
                            <b-table small borderless responsive
                                     :items="statusDistRows" :fields="statusDistFields"
                            >
                                <template #cell(status)="row">
                                    <span class="dfb-ov-nowrap">{{ factStatusLabel(row.item.status) }}</span>
                                </template>
                            </b-table>
                        </b-col>
                        <b-col cols="12" lg="6">
                            <div class="text-muted small mb-1">최근 실패 사유 Top</div>
                            <b-table small borderless responsive show-empty :items="failedReasonRows"
                                     :fields="failedFields" empty-text="최근 실패 없음"
                            >
                                <template #cell(error_type)="row">
                                    <div class="dfb-ov-break">{{ factErrorTypeLabel(row.item.error_type) }}</div>
                                    <code v-if="row.item.error_type
                                              && factErrorTypeLabel(row.item.error_type) !== row.item.error_type"
                                          class="text-muted small dfb-ov-break"
                                    >{{ row.item.error_type }}</code>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col cols="12">
                            <div class="text-muted small mb-1">프로젝트별 active (캠페인 기준 그룹)</div>
                            <b-table small borderless responsive show-empty
                                     :items="projectsByCampaign" :fields="projectGroupFields"
                                     empty-text="활성 프로젝트 없음"
                            >
                                <template #cell(campaign)="row">
                                    <span>{{ row.item.campaign_name
                                        || (row.item.campaign_id != null
                                            ? '캠페인 ' + row.item.campaign_id : '미지정') }}</span>
                                    <span v-if="row.item.campaign_name && row.item.campaign_id != null"
                                          class="text-muted small ml-1"
                                    >(ID: {{ row.item.campaign_id }})</span>
                                </template>
                                <template #cell(companies)="row">
                                    <span v-for="(c, idx) in row.item.companies"
                                          :key="c.company_id != null ? c.company_id : idx"
                                          class="dfb-company-chip mr-2"
                                    >
                                        {{ c.company_name
                                            || (c.company_id != null ? '회사 ' + c.company_id : '미지정') }}
                                        <strong>{{ c.task_count }}</strong>
                                    </span>
                                </template>
                                <template #cell(total)="row">
                                    <strong>{{ row.item.total }}</strong>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                    <p v-if="overview && overview.health" class="text-muted small mb-0 mt-2">
                        고착 진단 — stuck {{ overview.health.stuck_tasks || 0 }}건 ·
                        stale {{ overview.health.stale_tasks || 0 }}건 ·
                        age불명 {{ overview.health.unknown_age_tasks || 0 }}건
                    </p>
                </div>
            </b-collapse>
        </b-card>

        <!-- 필터 -->
        <b-card class="mb-3">
            <b-row align-v="end">
                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                    <b-form-group label="벤더" class="mb-0">
                        <b-form-select v-model="vendorFilter" :options="vendorOptions" size="sm"/>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                    <b-form-group label="상태" class="mb-0">
                        <b-form-select v-model="statusFilter" :options="statusOptions" size="sm"/>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="3" class="mb-3 mb-md-0">
                    <b-form-group label="페이지 크기" class="mb-0">
                        <b-form-select v-model="pagination.perPage" :options="perPageOptions"
                                       size="sm" @change="onPerPageChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="3" class="text-md-right">
                    <span class="text-muted small">벤더 미제출 대기 <strong>{{ queuedWithoutBatch }}</strong>건</span>
                </b-col>
            </b-row>
        </b-card>

        <!-- 목록 -->
        <b-card no-body>
            <!-- 받아 둔 목록이 있는 상태의 재조회 실패: 목록은 두고 실패만 알린다 -->
            <div v-if="loadError && loadedOnce"
                 class="d-flex align-items-center flex-wrap px-3 py-2 border-bottom small text-danger"
                 role="alert"
            >
                <span class="mr-2">목록을 새로 불러오지 못했습니다. ({{ loadError }}) 아래는 이전에 불러온 목록입니다.</span>
                <b-btn size="sm" variant="outline-secondary" :disabled="loading" @click="refresh()">다시 시도</b-btn>
            </div>
            <b-table :items="batches" :fields="fields" :busy="loading"
                     responsive hover small show-empty
            >
                <!-- 첫 조회 실패를 '배치가 없습니다'로 보이지 않게 — 사유와 다시 시도 -->
                <template #empty>
                    <page-empty-component compact :text="emptyText"
                                          :error="loadedOnce ? '' : loadError"
                                          @retry="refresh()"
                    />
                </template>
                <template #table-busy>
                    <div class="text-center py-4">
                        <b-spinner class="align-middle mr-2"/>
                        <span>불러오는 중...</span>
                    </div>
                </template>

                <template #cell(vendor)="row">
                    <b-badge :variant="row.item.vendor === 'openai' ? 'primary' : 'info'">
                        {{ vendorLabel(row.item.vendor) }}
                    </b-badge>
                </template>

                <template #cell(vendor_batch_id)="row">
                    <span class="small dfb-batch-id" :title="row.item.vendor_batch_id">
                        {{ row.item.vendor_batch_id }}
                    </span>
                </template>

                <!-- 상태 라벨이 곧 단일 상태 정보. 여러 상태가 섞인 배치(예: 일부 실패)일
                     때만 그 아래에 상태별 세부 건수 배지를 덧붙인다(상태별 건수 컬럼 대체). -->
                <template #cell(local_state)="row">
                    <b-badge :variant="stateMeta(row.item.local_state).variant">
                        {{ stateMeta(row.item.local_state).label }}
                    </b-badge>
                    <div v-if="hasMultiStatus(row.item)" class="mt-1">
                        <b-badge v-for="(count, status) in row.item.counts_by_status" :key="status"
                                 variant="light" class="border mr-1"
                        >
                            {{ status }} {{ count }}
                        </b-badge>
                    </div>
                </template>

                <template #cell(line_count)="row">
                    {{ row.item.line_count || 0 }}
                </template>

                <template #cell(storage_count)="row">
                    {{ row.item.storage_count || 0 }}
                </template>

                <template #cell(submitted_at)="row">
                    <span class="small dfb-time-col">
                        {{ row.item.submitted_at ? getDate(row.item.submitted_at) : '-' }}
                    </span>
                </template>

                <template #cell(completed_at)="row">
                    <span class="small dfb-time-col">
                        {{ row.item.completed_at ? getDate(row.item.completed_at) : '-' }}
                    </span>
                </template>

                <template #cell(actions)="row">
                    <b-btn size="sm" variant="outline-primary"
                           :disabled="isSyncing(row.item)"
                           @click="syncBatch(row.item)"
                    >
                        <b-spinner v-if="isSyncing(row.item)" small class="mr-1"/>
                        <b-icon-arrow-clockwise v-else class="mr-1"/>
                        지금 동기화
                    </b-btn>
                </template>
            </b-table>

            <div v-if="pagination.totalRows > pagination.perPage"
                 class="d-flex justify-content-center py-3"
            >
                <b-pagination v-model="pagination.currentPage" :total-rows="pagination.totalRows"
                              :per-page="pagination.perPage" :disabled="loading"
                              align="center" size="sm" class="mb-0"
                              @change="onPageChange"
                />
            </div>
        </b-card>
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue';
import { isSameQuery } from '@shared_mixins/contractor-dashboard/contractor-list-query';
import {getPerPageOptions, getDate} from '@shared/components/admin/pages/Management/common-data';
import {factStatusLabel, factErrorTypeLabel} from './batch-labels';

// 문서 사실 추출 화면이 실어 보낸 선택 키 — 이 화면은 조회에 쓰지 않고 '돌아가기' 링크용으로 보존만 한다.
const SELECTION_KEYS = ['period', 'project_code', 'campaign_id', 'company_id'];

// 상태값 → 라벨/뱃지 색상. 백엔드가 신규 상태를 추가해도 화면이 죽지 않도록
// 미등록 값은 원문 그대로 secondary 로 보여준다.
const STATE_META = {
    in_batch: {label: '처리 중', variant: 'info'},
    done: {label: '완료', variant: 'success'},
    partial_failed: {label: '일부 실패', variant: 'warning'},
    failed: {label: '실패', variant: 'danger'},
    unknown: {label: '알 수 없음', variant: 'secondary'},
};

export default {
    name: 'DocumentFactBatches',
    components: {PageEmptyComponent},
    data() {
        return {
            vendorOptions: [
                {value: null, text: '전체'},
                {value: 'openai', text: 'OpenAI'},
                {value: 'google', text: 'Google'},
            ],
            statusOptions: [
                {value: null, text: '전체'},
                {value: 'in_batch', text: '처리 중'},
                {value: 'done', text: '완료'},
                {value: 'partial_failed', text: '일부 실패'},
                {value: 'failed', text: '실패'},
                {value: 'unknown', text: '알 수 없음'},
            ],
            perPageOptions: getPerPageOptions(),
            vendorFilter: null,
            statusFilter: null,
            batches: [],
            summary: {},
            queuedWithoutBatch: 0,
            pagination: {currentPage: 1, perPage: 20, totalRows: 0},
            loading: false,
            // 조회 실패 사유. 첫 조회 실패는 빈 표 자리에, 받아 둔 목록이 있으면 표 위 한 줄로 보인다.
            loadError: '',
            loadedOnce: false,
            selection: {},
            silentRefreshing: false,
            syncingKeys: [],
            // 처리 중 배치가 있으면 60초마다 자동 갱신(항상 켜짐 — 토글 없음).
            pollTimer: null,
            visibilityHandler: null,
            loadRequestId: 0,
            // initFromQuery 가 필터를 프로그래밍적으로 세팅하는 동안 watcher 가
            // 조회를 중복 실행하지 않도록 막는 게이트. DocumentFacts.vue 의
            // initializing 패턴과 동일한 목적.
            initializing: true,
            // 복원 세대 — 복원 중 화면을 떠나거나 주소가 다시 바뀌면 늦은 복원이 조회·폴링을 시작하지 않게 한다.
            initToken: 0,
            // ── 운영 개요(/batch/overview, 작업 단위 스냅샷) ──
            overview: null,
            overviewLoading: false,
            overviewRequestId: 0,
            // 운영 개요 상세(진단용 표 3종)는 기본 접힘 — 본문(목록)을 위로 올리기 위함.
            overviewDetailOpen: false,
            statusDistFields: [
                {key: 'status', label: '상태'},
                {key: 'active', label: 'active', class: 'text-right', thStyle: {width: '70px'}},
                {key: 'terminal', label: '최근종료', class: 'text-right', thStyle: {width: '80px'}},
            ],
            failedFields: [
                {key: 'error_type', label: '유형'},
                {key: 'count', label: '건수', class: 'text-right', thStyle: {width: '70px'}},
            ],
            projectGroupFields: [
                {key: 'campaign', label: '캠페인', thStyle: {width: '220px'}},
                {key: 'companies', label: '회사 (active 건수)'},
                {key: 'total', label: '합계', class: 'text-right', thStyle: {width: '80px'}},
            ],
            fields: [
                {key: 'vendor', label: '벤더', thStyle: {width: '90px'}},
                {key: 'vendor_batch_id', label: '벤더 배치 ID'},
                {key: 'local_state', label: '상태', thStyle: {width: '110px'}},
                {key: 'line_count', label: '라인 수', thStyle: {width: '90px'}},
                {key: 'storage_count', label: '첨부 수', thStyle: {width: '90px'}},
                {key: 'submitted_at', label: '제출 일시', thStyle: {width: '160px'}},
                {key: 'completed_at', label: '완료 일시', thStyle: {width: '160px'}},
                {key: 'actions', label: '', thStyle: {width: '140px'}},
            ],
        };
    },
    computed: {
        hasInProgress() {
            return Number((this.summary && this.summary.in_batch) || 0) > 0;
        },
        emptyText() {
            return this.vendorFilter || this.statusFilter
                ? '조건에 맞는 배치가 없습니다. (필터를 조정해 보세요)'
                : '배치가 없습니다.';
        },
        selectionQuery() {
            const query = {};
            SELECTION_KEYS.forEach(key => {
                if (this.selection[key]) query[key] = this.selection[key];
            });
            return query;
        },
        // 화면 상태 그대로의 주소 — 기본값(전체·1페이지·20개·접힘)은 생략한다.
        stateQuery() {
            const query = {...this.selectionQuery};
            if (this.vendorFilter) query.vendor = this.vendorFilter;
            if (this.statusFilter) query.status = this.statusFilter;
            if (this.pagination.currentPage > 1) query.page = String(this.pagination.currentPage);
            if (this.pagination.perPage !== 20) query.per_page = String(this.pagination.perPage);
            if (this.overviewDetailOpen) query.ov = '1';
            return query;
        },
        overviewLookbackHours() {
            return (this.overview && this.overview.scope && this.overview.scope.recent
                && this.overview.scope.recent.lookback_hours) || 168;
        },
        // 한 줄 KPI 스트립 — 기존 운영개요 지표타일 + 요약카드 지표를 통합.
        // 작업 단위(overview)와 배치 단위(전체 배치) 값을 한 곳에서만 노출해 중복 제거.
        kpiCards() {
            const active = (this.overview && this.overview.active) || {};
            const statuses = active.status_counts || {};
            const health = (this.overview && this.overview.health) || {};
            const terminal = (this.overview && this.overview.recent
                && this.overview.recent.terminal_status_counts) || {};
            return [
                {key: 'active', label: '진행 작업', value: active.task_count || 0, variant: 'primary'},
                {key: 'in_batch', label: factStatusLabel('in_batch'), value: statuses.in_batch || 0, variant: 'info'},
                {key: 'done', label: '완료 (최근)', value: terminal.done || 0, variant: 'success'},
                {key: 'failed', label: '실패 (최근)', value: terminal.failed || 0, variant: 'danger'},
                {key: 'prepared', label: factStatusLabel('prepared'), value: statuses.prepared || 0, variant: 'secondary'},
                {key: 'health', label: '고착', value: health.affected_tasks || 0, variant: 'warning'},
                {key: 'total', label: '전체 배치', value: this.pagination.totalRows || 0, variant: 'dark'},
            ];
        },
        statusDistRows() {
            const active = (this.overview && this.overview.active
                && this.overview.active.status_counts) || {};
            const terminal = (this.overview && this.overview.recent
                && this.overview.recent.terminal_status_counts) || {};
            const order = ['queued', 'preparing', 'prepared', 'running', 'in_batch',
                'done', 'failed', 'superseded'];
            return order.map(status => ({
                status,
                active: active[status] || 0,
                terminal: terminal[status] || 0,
            }));
        },
        failedReasonRows() {
            return (this.overview && this.overview.recent
                && this.overview.recent.failed_reasons) || [];
        },
        projectRows() {
            // 이름은 백엔드가 내려줄 때만 채워짐 — 미배포 시엔 name null → 셀 템플릿이 ID로 폴백.
            return ((this.overview && this.overview.projects) || []).map(row => ({
                company_id: row.company_id != null ? row.company_id : null,
                company_name: row.company_name || null,
                campaign_id: row.campaign_id != null ? row.campaign_id : null,
                campaign_name: row.campaign_name || null,
                task_count: row.task_count,
            }));
        },
        // 캠페인 기준으로 회사를 묶는다 — 같은 캠페인이 매 행마다 반복되던 것을 없애고,
        // 캠페인당 소속 회사(active 건수)와 합계를 한 행으로 보여준다. 합계 내림차순 정렬.
        projectsByCampaign() {
            const groups = new Map();
            for (const row of this.projectRows) {
                const gid = row.campaign_id != null ? `id:${row.campaign_id}` : '__none__';
                if (!groups.has(gid)) {
                    groups.set(gid, {
                        campaign_id: row.campaign_id,
                        campaign_name: row.campaign_name,
                        companies: [],
                        total: 0,
                    });
                }
                const g = groups.get(gid);
                g.companies.push({
                    company_id: row.company_id,
                    company_name: row.company_name,
                    task_count: row.task_count,
                });
                g.total += Number(row.task_count || 0);
            }
            return Array.from(groups.values()).sort((a, b) => b.total - a.total);
        },
    },
    watch: {
        vendorFilter() {
            if (!this.initializing) this.onFilterChange();
        },
        statusFilter() {
            if (!this.initializing) this.onFilterChange();
        },
        // 같은 화면에서 주소만 바뀌면(메뉴 재클릭·뒤로가기) 그 주소대로 다시 맞춘다. 스스로 쓴 주소는 무시.
        '$route.query'(query) {
            if (!isSameQuery(query || {}, this.stateQuery)) this.initFromQuery();
        },
    },
    created() {
        // 리스너를 먼저 동기 등록한다 — initFromQuery 의 loadBatches 가 끝나기
        // 전에 화면을 이탈하면 beforeDestroy 가 더 먼저 실행되는데, 리스너 등록이
        // 그 이후(비동기 완료 시점)면 이미 파괴된 컴포넌트에 리스너가 새로 걸려
        // 정리되지 않고 폴링 타이머까지 새로 시작될 수 있다(누수).
        this.setupVisibilityListener();
        this.initFromQuery();
    },
    beforeDestroy() {
        // 초기 loadBatches 가 아직 진행 중일 때 화면을 이탈하면, 그 요청이
        // 파괴 이후에 늦게 도착해 state 반영 + reconcilePolling() 을 실행해
        // 새 폴링 타이머를 시작시킬 수 있다(파괴된 컴포넌트라 다시 정리되지
        // 않는 누수). loadRequestId 를 무효화해 loadBatches 의 기존
        // stale-response 가드(state 반영·reconcilePolling 호출 전 early-return)가
        // 이 늦은 응답도 stale 로 판정하게 한다.
        this.loadRequestId += 1;
        this.overviewRequestId += 1;
        this.initToken += 1;
        this.stopPolling();
        if (this.visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }
    },
    methods: {
        getDate,
        factStatusLabel,
        factErrorTypeLabel,
        formatNum(value) {
            return Number(value || 0).toLocaleString();
        },
        // 한 배치에 상태가 2종 이상 섞였는지(예: 일부 실패 = done+failed).
        // 단일 상태면 '상태' 컬럼 라벨이 곧 정보이므로 세부 배지를 숨긴다.
        hasMultiStatus(item) {
            return !!item.counts_by_status && Object.keys(item.counts_by_status).length > 1;
        },
        // 목록과 개요를 함께 새로고침(수동 버튼용).
        refresh() {
            this.loadBatches();
            this.loadOverview();
        },
        async loadOverview() {
            const requestId = ++this.overviewRequestId;
            this.overviewLoading = true;
            try {
                const params = {};
                if (this.vendorFilter) params.vendor = this.vendorFilter;
                const {data} = await this.$axios.get('/document-facts/batch/overview', {params});
                if (requestId !== this.overviewRequestId) return;
                this.overview = data || null;
            } catch (e) {
                if (requestId !== this.overviewRequestId) return;
                // 개요 실패는 목록/동기화를 막지 않는다 — 이전 값 유지 + 토스트 1회.
                const msg = getErrorMessage(e, '운영 개요를 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', '개요 조회 오류', msg);
            } finally {
                if (requestId === this.overviewRequestId) this.overviewLoading = false;
            }
        },
        vendorLabel(vendor) {
            if (vendor === 'openai') return 'OpenAI';
            if (vendor === 'google') return 'Google';
            return vendor || '-';
        },
        stateMeta(state) {
            return STATE_META[state] || {label: state || '-', variant: 'secondary'};
        },
        rowKey(item) {
            return `${item.vendor}:${item.vendor_batch_id}`;
        },
        isSyncing(item) {
            return this.syncingKeys.includes(this.rowKey(item));
        },
        // ── query-parameter 로 랜딩 상태 복원 (페이지 이동/뒤로가기 안전) ──
        async initFromQuery() {
            const token = ++this.initToken;
            this.initializing = true;
            const q = this.$route.query || {};
            // vue-router 는 중복 키를 배열로 파싱하므로 모든 값을 스칼라로
            // 강제하고 화이트리스트/숫자로 검증한다.
            const first = v => (Array.isArray(v) ? v[0] : v);
            const asStr = v => (typeof first(v) === 'string' ? first(v) : '');
            const asInt = v => {
                const n = Math.floor(Number(first(v)));
                return Number.isFinite(n) && n > 0 ? n : null;
            };
            // 주소에 없는 키는 기본값으로 — 뒤로가기로 필터 없는 주소에 오면 필터도 풀린다.
            const vendor = asStr(q.vendor);
            this.vendorFilter = this.vendorOptions.some(o => o.value === vendor) ? vendor : null;
            const status = asStr(q.status);
            this.statusFilter = this.statusOptions.some(o => o.value === status) ? status : null;
            this.pagination.currentPage = asInt(q.page) || 1;
            const perPage = asInt(q.per_page);
            this.pagination.perPage = this.perPageOptions.some(o => o.value === perPage) ? perPage : 20;
            this.overviewDetailOpen = asStr(q.ov) === '1';
            this.selection = SELECTION_KEYS.reduce((acc, key) => {
                const value = asStr(q[key]);
                if (value) acc[key] = value;
                return acc;
            }, {});
            // 위 필터 대입의 watcher 는 다음 틱에 돈다 — 그때도 복원 중으로 보이게 한 틱 뒤에 해제한다
            // (먼저 풀면 watcher 가 onFilterChange 로 1페이지로 되돌리고 조회를 한 번 더 한다).
            await this.$nextTick();
            if (token !== this.initToken) return;
            this.initializing = false;
            await this.loadBatches();
            if (token === this.initToken) this.loadOverview();
        },
        // 사용자 조작(필터·페이지·페이지 크기)은 push, 화면 보정(범위 밖 페이지·상세 펼침)은 replace.
        syncQuery(push = true) {
            if (this.initializing) return;
            if (isSameQuery(this.$route.query || {}, this.stateQuery)) return;
            this.$router[push ? 'push' : 'replace']({query: this.stateQuery}).catch(() => {});
        },
        toggleOverviewDetail() {
            this.overviewDetailOpen = !this.overviewDetailOpen;
            this.syncQuery(false);
        },
        onFilterChange() {
            this.pagination.currentPage = 1;
            this.syncQuery();
            this.loadBatches();
            this.loadOverview();
        },
        onPageChange(page) {
            this.pagination.currentPage = page;
            this.syncQuery();
            this.loadBatches();
        },
        onPerPageChange() {
            this.pagination.currentPage = 1;
            this.syncQuery();
            this.loadBatches();
        },
        async loadBatches({silent = false} = {}) {
            // 빠른 필터 변경 시 늦게 도착한 과거 응답이 최신 목록을 덮지 않도록
            // 증가형 requestId 로 최신 응답만 반영한다.
            const requestId = ++this.loadRequestId;
            if (silent) this.silentRefreshing = true;
            else this.loading = true;
            try {
                const params = {
                    page: this.pagination.currentPage,
                    per_page: this.pagination.perPage,
                };
                if (this.vendorFilter) params.vendor = this.vendorFilter;
                if (this.statusFilter) params.status = this.statusFilter;
                const {data} = await this.$axios.get('/document-facts/batches', {params});
                if (requestId !== this.loadRequestId) return;
                this.batches = (data && data.batches) || [];
                this.summary = (data && data.summary) || {};
                this.queuedWithoutBatch = (data && data.queued_without_batch) || 0;
                this.pagination.totalRows = (data && data.total_batches) || 0;
                if (data && data.page) this.pagination.currentPage = data.page;
                if (data && data.per_page) this.pagination.perPage = data.per_page;
                this.reconcilePolling();
                // 필터 변경 등으로 현재 페이지가 범위를 벗어나면 마지막 유효
                // 페이지로 한 번만 재조회한다(무한 루프 방지를 위해 보정 후 재귀 1회).
                const lastPage = Math.max(
                    1, Math.ceil(this.pagination.totalRows / this.pagination.perPage));
                this.loadError = '';
                this.loadedOnce = true;
                if (this.pagination.totalRows > 0 && this.pagination.currentPage > lastPage) {
                    this.pagination.currentPage = lastPage;
                    this.syncQuery(false);
                    await this.loadBatches({silent});
                }
            } catch (e) {
                if (requestId !== this.loadRequestId) return;
                if (e && e.response && e.response.status === 401) return;
                const msg = getErrorMessage(e, '배치 목록을 불러오지 못했습니다.');
                this.loadError = msg;
                // 자동 갱신(silent) 실패는 표 위 안내줄로만 알린다 — 60초마다 토스트가 쌓이지 않게.
                if (!silent) this.$EventBus.$emit('toast-failure', '조회 오류', msg);
            } finally {
                if (requestId === this.loadRequestId) {
                    this.loading = false;
                    this.silentRefreshing = false;
                }
            }
        },
        async syncBatch(item) {
            const key = this.rowKey(item);
            if (this.syncingKeys.includes(key)) return;
            this.syncingKeys.push(key);
            try {
                const {data} = await this.$axios.post('/document-facts/batches/sync', {
                    vendor: item.vendor,
                    vendor_batch_id: item.vendor_batch_id,
                });
                this.handleSyncResult(data);
                // 202 는 접수일 뿐 완료가 아니므로, 폴링 여부와 무관하게 한 번 재조회한다.
                await this.loadBatches({silent: true});
            } catch (e) {
                this.handleSyncError(e);
            } finally {
                this.syncingKeys = this.syncingKeys.filter(k => k !== key);
            }
        },
        handleSyncResult(data) {
            const status = data && data.status;
            const requestId = data && data.request_id;
            const suffix = requestId ? ` (request_id: ${requestId})` : '';
            if (status === 'accepted') {
                this.$EventBus.$emit('single-toast-on', {
                    variant: 'success',
                    title: '동기화 요청 접수',
                    content: `벤더 배치 동기화 요청을 접수했습니다.${suffix}`,
                });
            } else if (status === 're_dispatched') {
                this.$EventBus.$emit('single-toast-on', {
                    variant: 'success',
                    title: '동기화 재요청 접수',
                    content: `이전 요청이 끊겨 동기화를 다시 요청했습니다.${suffix}`,
                });
            } else if (status === 'already_in_progress') {
                this.$EventBus.$emit('single-toast-on', {
                    variant: 'warning',
                    title: '이미 동기화 진행 중',
                    content: `해당 배치는 이미 동기화가 진행 중입니다.${suffix}`,
                });
            } else {
                this.$EventBus.$emit('single-toast-on', {
                    variant: 'warning',
                    title: '동기화 요청 접수',
                    content: `알 수 없는 응답 상태(${status || '없음'})입니다. 목록을 갱신합니다.${suffix}`,
                });
            }
        },
        handleSyncError(e) {
            const status = e.response && e.response.status;
            const serverMsg = getErrorMessage(e, '');
            let title = '동기화 요청 실패';
            let content = serverMsg || '동기화 요청 중 오류가 발생했습니다.';
            if (status === 404) {
                title = '로컬 배치를 찾을 수 없음';
                content = serverMsg || '이 배치는 더 이상 로컬에 존재하지 않습니다. 목록을 새로고침해 주세요.';
            } else if (status === 503) {
                title = '동기화 요청 발행 실패';
                content = serverMsg || '동기화 요청을 발행하지 못했습니다. 잠시 후 다시 시도해 주세요.';
            } else if (status === 403) {
                title = '권한 없음';
                content = serverMsg || '문서 사실 추출 관리 권한이 없습니다.';
            }
            this.$EventBus.$emit('single-toast-on', {variant: 'danger', title, content});
        },
        // ── 처리 중 자동 갱신(항상 켜진 60초 폴링) ──────────────────────
        // 토글 없이 기본 동작. 처리 중 배치가 있고 탭이 보일 때만 폴링해 불필요한
        // GET 을 피한다(처리 중이 0이면 갱신할 것이 없으므로 폴링 불필요).
        reconcilePolling() {
            const visible = typeof document === 'undefined' || !document.hidden;
            const shouldPoll = this.hasInProgress && visible;
            if (shouldPoll) this.startPolling();
            else this.stopPolling();
        },
        startPolling() {
            if (this.pollTimer) return; // 이미 돌고 있음 — 중복 GET 방지
            this.pollTimer = setInterval(() => {
                if (this.loading || this.silentRefreshing) return; // 중복 GET 방지
                this.loadBatches({silent: true});
                this.loadOverview();
            }, 60000);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        setupVisibilityListener() {
            if (typeof document === 'undefined') return;
            this.visibilityHandler = () => {
                if (document.hidden) this.stopPolling();
                else this.reconcilePolling();
            };
            document.addEventListener('visibilitychange', this.visibilityHandler);
        },
    },
};
</script>

<style scoped>
/* 항상 켜진 자동 갱신 표시(초록 pill + 깜빡이는 점) */
.dfb-auto-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #155724;
    background: #e8f5ec;
    border: 1px solid #b7e0c3;
    border-radius: 1rem;
    padding: 2px 10px;
}
.dfb-auto-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #28a745;
    display: inline-block;
    animation: dfb-pulse 1.6s infinite;
}
@keyframes dfb-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .3; }
}
/* KPI 스트립 */
.dfb-kpi-strip {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
}
.dfb-kpi {
    flex: 1;
    min-width: 110px;
    padding: 8px 16px;
    border-right: 1px solid #eef0f2;
}
.dfb-kpi:last-child {
    border-right: none;
}
.dfb-kpi-val {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.1;
}
.dfb-kpi-unit {
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
    margin-left: 2px;
}
/* 접이식 헤더 */
.dfb-collapse-head {
    display: flex;
    align-items: center;
    padding: 12px 18px;
    cursor: pointer;
    user-select: none;
}
.dfb-company-chip {
    white-space: nowrap;
}
.dfb-ov-break {
    word-break: break-all;
}
.dfb-ov-nowrap {
    white-space: nowrap;
}
.dfb-batch-id {
    word-break: break-all;
}
.dfb-time-col {
    white-space: nowrap;
}
</style>
